/**
 * KDS - Kitchen Display System
 * Tela de cozinha em TELA CHEIA (imersiva), otimizada para celular em paisagem.
 * Cada chefe vê SOMENTE os itens do seu setor. O gestor vê tudo (com filtro).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChefHat, Bell, BellOff, CheckCircle2, RotateCcw, Clock, Bike, Store, ShoppingBag, RefreshCw, X, History } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

function elapsedInfo(createdAtIso, now) {
  if (!createdAtIso) return { label: '--', color: 'text-gray-400', mins: 0 };
  const created = new Date(createdAtIso).getTime();
  const mins = Math.max(0, Math.floor((now - created) / 60000));
  let color = 'text-emerald-600';
  if (mins >= 20) color = 'text-red-600';
  else if (mins >= 10) color = 'text-amber-600';
  return { label: mins < 1 ? 'agora' : `${mins} min`, color, mins };
}

const DELIVERY_META = {
  delivery: { icon: Bike, label: 'Delivery' },
  mesa: { icon: Store, label: 'Mesa' },
  retirada: { icon: ShoppingBag, label: 'Retirada' },
};

function ticketTitle(ticket) {
  const isMesa = ticket.delivery_type === 'mesa' || !!ticket.table_number;
  if (isMesa && ticket.table_number) {
    const t = String(ticket.table_number).trim();
    return /^mesa/i.test(t) ? t : `Mesa ${t}`;
  }
  const num = String(ticket.order_number || '').replace(/^#/, '');
  return `#${num}`;
}

const KDSScreen = ({ onExit, isGestor: isGestorProp }) => {
  const [config, setConfig] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [stationFilter, setStationFilter] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('kds_sound') === '1');
  const [busyIds, setBusyIds] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [historyTickets, setHistoryTickets] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const audioRef = useRef(null);
  const knownIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const playSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/sounds/notification.mp3');
        audioRef.current.volume = 0.7;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch (e) { /* noop */ }
  }, [soundEnabled]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/kds/config`, authHeaders());
        setConfig(res.data);
      } catch (e) {
        const msg = e.response?.data?.detail;
        toast.error(typeof msg === 'string' ? msg : 'Erro ao carregar KDS');
      }
    })();
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      const url = stationFilter ? `${API}/kds/orders?station_user_id=${stationFilter}` : `${API}/kds/orders`;
      const res = await axios.get(url, authHeaders());
      const data = res.data?.tickets || [];
      const pendingIds = data.filter(t => !t.station_done).map(t => t.order_id);
      if (!firstLoadRef.current) {
        if (pendingIds.some(id => !knownIdsRef.current.has(id))) playSound();
      }
      knownIdsRef.current = new Set(pendingIds);
      firstLoadRef.current = false;
      setTickets(data);
    } catch (e) { /* silencioso no polling */ }
    finally { setLoading(false); }
  }, [stationFilter, playSound]);

  useEffect(() => {
    firstLoadRef.current = true;
    setLoading(true);
    fetchTickets();
    const id = setInterval(fetchTickets, 8000);
    return () => clearInterval(id);
  }, [fetchTickets]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('kds_sound', next ? '1' : '0');
    if (next) {
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio('/sounds/notification.mp3');
          audioRef.current.volume = 0.7;
        }
        audioRef.current.play().then(() => { audioRef.current.pause(); audioRef.current.currentTime = 0; }).catch(() => {});
      } catch (e) { /* noop */ }
      toast.success('Som de aviso ligado');
    } else {
      toast('Som de aviso desligado');
    }
  };

  const markReady = async (ticket) => {
    setBusyIds(b => ({ ...b, [ticket.order_id]: true }));
    setTickets(ts => ts.filter(t => !(t.order_id === ticket.order_id && t.is_addition === ticket.is_addition)));
    try {
      const body = {};
      if (stationFilter) body.station_user_id = stationFilter;
      if (Array.isArray(ticket.item_indexes)) body.item_indexes = ticket.item_indexes;
      await axios.post(`${API}/kds/orders/${ticket.order_id}/ready`, body, authHeaders());
    } catch (e) { toast.error('Erro ao marcar pronto'); fetchTickets(); }
    finally { setBusyIds(b => ({ ...b, [ticket.order_id]: false })); setTimeout(fetchTickets, 400); }
  };

  const undoReady = async (ticket) => {
    setBusyIds(b => ({ ...b, [ticket.order_id]: true }));
    try {
      const body = {};
      if (stationFilter) body.station_user_id = stationFilter;
      if (Array.isArray(ticket.item_indexes)) body.item_indexes = ticket.item_indexes;
      await axios.post(`${API}/kds/orders/${ticket.order_id}/undo`, body, authHeaders());
    } catch (e) { toast.error('Erro ao voltar'); }
    finally {
      setBusyIds(b => ({ ...b, [ticket.order_id]: false }));
      fetchTickets();
      fetchHistory();
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const url = stationFilter ? `${API}/kds/history?station_user_id=${stationFilter}` : `${API}/kds/history`;
      const res = await axios.get(url, authHeaders());
      setHistoryTickets(res.data?.tickets || []);
    } catch (e) { /* silencioso */ }
    finally { setHistoryLoading(false); }
  }, [stationFilter]);

  useEffect(() => {
    if (!showHistory) return;
    fetchHistory();
    const id = setInterval(fetchHistory, 10000);
    return () => clearInterval(id);
  }, [showHistory, fetchHistory]);

  useEffect(() => {
    fetchHistory();
    const id = setInterval(fetchHistory, 20000);
    return () => clearInterval(id);
  }, [fetchHistory]);

  const todo = tickets.filter(t => !t.station_done);
  const done = historyTickets;

  const isGestor = config?.is_gestor ?? isGestorProp;
  const stationLabel = config?.station_label
    || (stationFilter ? (config?.stations?.find(s => s.id === stationFilter)?.name) : 'Todos os setores');
  const cozinhaSemSetor = config && !isGestor && (config.my_categories?.length === 0) && (config.my_products?.length === 0);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-slate-200 flex flex-col" data-testid="kds-screen">
      <header className="flex-shrink-0 bg-slate-900 text-white px-2 py-1.5 sm:px-3 flex items-center justify-between gap-2 shadow-lg">
        <div className="flex items-center gap-2 min-w-0">
          <ChefHat className="w-5 h-5 sm:w-6 sm:h-6 text-red-400 flex-shrink-0" />
          <div className="min-w-0 leading-none">
            <div className="font-extrabold text-sm sm:text-base tracking-tight">COZINHA</div>
            <div className="text-[10px] sm:text-[11px] text-slate-300 truncate max-w-[38vw]">{stationLabel}</div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {isGestor && (config?.stations?.length > 0) && (
            <select
              data-testid="kds-station-filter"
              value={stationFilter}
              onChange={e => setStationFilter(e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded-lg text-[11px] sm:text-sm px-2 py-1.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500 max-w-[34vw]"
            >
              <option value="">Todos os setores</option>
              {config.stations.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
            </select>
          )}
          <button onClick={fetchTickets} title="Atualizar" className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200" data-testid="kds-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowHistory(true)}
            title="Histórico de prontos"
            data-testid="kds-history-open"
            className="relative p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200"
          >
            <History className="w-4 h-4" />
            {done.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center">
                {done.length}
              </span>
            )}
          </button>
          <button
            onClick={toggleSound}
            title={soundEnabled ? 'Desligar som' : 'Ligar som'}
            data-testid="kds-sound-toggle"
            className={`p-2 rounded-lg transition-colors ${soundEnabled ? 'bg-red-500/20 text-red-300' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
          >
            {soundEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          </button>
          <button onClick={onExit} title="Sair da cozinha" data-testid="kds-exit" className="p-2 rounded-lg bg-red-600 hover:bg-red-500 text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      </header>

      {cozinhaSemSetor && (
        <div className="m-2 p-3 rounded-xl bg-amber-100 border border-amber-300 text-amber-800 text-sm flex-shrink-0">
          ⚠️ Seu setor ainda não foi configurado. Peça ao gestor para definir suas categorias na aba <b>Equipe</b>.
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto px-2 pt-2">
        <div className="sticky top-0 z-10 bg-slate-200 flex items-center gap-2 px-1 py-2 mb-1">
          <span className="w-3 h-3 rounded-full bg-amber-500 animate-pulse" />
          <h2 className="font-extrabold text-slate-800 text-sm sm:text-base uppercase tracking-wide">A Preparar</h2>
          <span className="ml-auto text-xs font-bold text-white bg-amber-500 rounded-full px-2.5 py-0.5">{todo.length}</span>
        </div>
        {loading ? (
          <div className="text-center text-slate-400 py-16 text-sm">Carregando…</div>
        ) : todo.length === 0 ? (
          <div className="text-center text-slate-400 py-16 bg-white rounded-2xl border-2 border-dashed border-slate-300 mt-2">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-400" />
            <div className="font-semibold">Tudo em dia!</div>
            <div className="text-xs mt-1">Nenhum pedido na fila.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 landscape:grid-cols-2 xl:grid-cols-3 gap-2.5 pb-6 items-start">
            {todo.map(t => (
              <TicketCard key={`${t.order_id}-${t.is_addition ? 'add' : 'base'}`} ticket={t} now={now} busy={!!busyIds[t.order_id]} onReady={() => markReady(t)} />
            ))}
          </div>
        )}
      </div>

      {showHistory && (
        <div className="absolute inset-0 z-20 flex flex-col bg-slate-200" data-testid="kds-history-panel">
          <div className="flex-shrink-0 bg-emerald-600 text-white px-3 py-2.5 flex items-center justify-between shadow-md">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5" />
              <span className="font-extrabold text-base uppercase tracking-wide">Histórico — Prontos</span>
              <span className="text-xs font-bold text-emerald-700 bg-white rounded-full px-2 py-0.5">{done.length}</span>
            </div>
            <button onClick={() => setShowHistory(false)} data-testid="kds-history-close" className="p-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-2">
            {done.length === 0 ? (
              <div className="text-center text-slate-400 py-16 bg-white rounded-2xl border-2 border-dashed border-slate-300 mt-2">
                Nenhum pedido finalizado ainda.
              </div>
            ) : (
              <div className="grid grid-cols-1 landscape:grid-cols-2 xl:grid-cols-3 gap-2.5 pb-6 items-start">
                {done.map(t => (
                  <TicketCard key={`hist-${t.order_id}`} ticket={t} now={now} done busy={!!busyIds[t.order_id]} onUndo={() => undoReady(t)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

const TicketCard = ({ ticket, now, done, busy, onReady, onUndo }) => {
  const el = elapsedInfo(ticket.created_at, now);
  const dmeta = DELIVERY_META[ticket.delivery_type] || DELIVERY_META.delivery;
  const DIcon = dmeta.icon;
  const isAddition = !!ticket.is_addition;
  const title = isAddition ? (ticket.addition_title || 'Acréscimos') : ticketTitle(ticket);
  const isMesa = ticket.delivery_type === 'mesa' || !!ticket.table_number;
  const subRef = isMesa ? (ticket.customer_name || '') : (ticket.customer_name || '');

  return (
    <div
      data-testid={`kds-ticket-${ticket.order_id}`}
      className={`rounded-2xl bg-white shadow-md border-l-[6px] overflow-hidden ${
        done ? 'border-emerald-500 opacity-90'
        : isAddition ? 'border-violet-500'
        : (el.mins >= 20 ? 'border-red-500' : el.mins >= 10 ? 'border-amber-500' : 'border-emerald-500')
      }`}
    >
      <div className={`flex items-center justify-between gap-2 px-3 py-2 border-b ${isAddition && !done ? 'bg-violet-50 border-violet-100' : 'bg-slate-50 border-slate-100'}`}>
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className="font-black text-slate-900 text-lg sm:text-xl truncate">{title}</span>
          {isAddition && !done && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-violet-600 rounded-full px-2 py-0.5 flex-shrink-0 uppercase tracking-wide">
              + Acréscimos
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-200 rounded-full px-2 py-0.5 flex-shrink-0">
            <DIcon className="w-3 h-3" /> {dmeta.label}
          </span>
        </div>
        <div className={`flex items-center gap-1 font-bold text-sm flex-shrink-0 ${el.color}`}>
          <Clock className="w-4 h-4" /> {el.label}
        </div>
      </div>

      {subRef ? <div className="px-3 pt-1.5 text-xs text-slate-500 truncate">{subRef}</div> : null}

      <ul className="px-3 py-2 space-y-2">
        {ticket.items.map((it) => (
          <li key={it.index} className="flex items-start gap-2.5">
            <span className="flex-shrink-0 min-w-[2.2rem] h-8 px-1.5 rounded-lg bg-slate-900 text-white font-black text-base flex items-center justify-center">
              {it.quantity}x
            </span>
            <div className="min-w-0 flex-1">
              <div className={`font-bold text-slate-800 text-[15px] sm:text-base leading-tight ${done ? 'line-through text-slate-400' : ''}`}>
                {it.name}
              </div>
              {it.additionals && it.additionals.length > 0 && (
                <div className="text-xs text-slate-500 mt-0.5">+ {it.additionals.join(', ')}</div>
              )}
              {it.notes && (
                <div className="text-xs font-semibold text-red-600 mt-0.5 bg-red-50 rounded px-1.5 py-0.5 inline-block">
                  📝 {it.notes}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      {ticket.notes && (
        <div className="mx-3 mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
          Obs.: {ticket.notes}
        </div>
      )}

      <div className="p-2">
        {done ? (
          <button onClick={onUndo} disabled={busy} data-testid={`kds-undo-${ticket.order_id}`}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm disabled:opacity-60">
            <RotateCcw className="w-4 h-4" /> Voltar para produção
          </button>
        ) : (
          <button onClick={onReady} disabled={busy} data-testid={`kds-ready-${ticket.order_id}`}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-black text-lg shadow-lg shadow-emerald-500/30 disabled:opacity-60 transition-colors">
            <CheckCircle2 className="w-6 h-6" /> PRONTO
          </button>
        )}
      </div>
    </div>
  );
};

export default KDSScreen;
